<template>
  <div class="lease-image-upload" :class="{ 'is-readonly': readonly || disabled }">
    <el-upload
      :disabled="disabled || readonly"
      :action="uploadUrl"
      list-type="picture-card"
      :file-list="fileList"
      :limit="limit"
      :with-credentials="true"
      :before-upload="beforeUpload"
      :on-success="handleSuccess"
      :on-remove="handleRemove"
      :on-exceed="handleExceed"
      :on-preview="handlePreview"
      accept="image/jpeg,image/png,image/jpg"
    >
      <i class="el-icon-plus"></i>
    </el-upload>
    <div class="tip" v-if="tip && !readonly">{{ tip }}</div>
    <el-dialog
      :visible.sync="previewVisible"
      append-to-body
      width="640px"
      custom-class="lease-ylw-dialog"
      title="图片预览"
    >
      <img v-if="previewUrl" :src="previewUrl" alt="" class="preview-img" />
    </el-dialog>
  </div>
</template>

<script>
import baseUrl from "@/service/base-url";
import { loadLeaseResourceObjectUrl } from "@/service/fishPondLease.js";

export default {
  name: "LeaseImageUpload",
  props: {
    value: { type: String, default: "" },
    limit: { type: Number, default: 3 },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    tip: { type: String, default: "支持 jpg/png，单张不超过 3MB" },
    maxSizeMb: { type: Number, default: 3 }
  },
  data() {
    return {
      uploadUrl: baseUrl.imgUpload,
      fileList: [],
      syncing: false,
      blobUrls: [],
      previewVisible: false,
      previewUrl: ""
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.syncing) return;
        this.syncFromValue(val);
      }
    }
  },
  beforeDestroy() {
    this.revokeBlobs();
  },
  methods: {
    revokeBlobs() {
      (this.blobUrls || []).forEach(function(u) {
        try {
          URL.revokeObjectURL(u);
        } catch (e) {}
      });
      this.blobUrls = [];
    },
    rememberBlob(url) {
      if (url && String(url).indexOf("blob:") === 0) {
        this.blobUrls.push(url);
      }
      return url;
    },
    pickRawName(res, file) {
      if (res && (res.name || res.url || res.key)) {
        return res.name || res.url || res.key;
      }
      if (file && file.response) {
        const r = file.response;
        return r.name || r.url || r.key || "";
      }
      return "";
    },
    syncFromValue(val) {
      var self = this;
      this.revokeBlobs();
      if (!val) {
        this.fileList = [];
        return;
      }
      const raws = String(val)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
      // 先占位，再异步换成带登录态的 blob 预览
      this.fileList = raws.map((raw, index) => ({
        uid: "lease-img-" + index + "-" + raw,
        name: raw.split("/").pop() || `img_${index}`,
        url: "",
        rawUrl: raw,
        status: "uploading"
      }));
      Promise.all(
        raws.map(function(raw) {
          return loadLeaseResourceObjectUrl(raw)
            .then(function(url) {
              return { raw: raw, url: self.rememberBlob(url) };
            })
            .catch(function() {
              return { raw: raw, url: "" };
            });
        })
      ).then(function(items) {
        self.fileList = items.map(function(item, index) {
          return {
            uid: "lease-img-" + index + "-" + item.raw,
            name: item.raw.split("/").pop() || `img_${index}`,
            url: item.url,
            rawUrl: item.raw,
            status: item.url ? "success" : "fail"
          };
        });
      });
    },
    beforeUpload(file) {
      const isImg = /image\/(jpeg|png|jpg)/.test(file.type);
      if (!isImg) {
        this.$message.error("只能上传 JPG/PNG 图片");
        return false;
      }
      const ok = file.size / 1024 / 1024 < this.maxSizeMb;
      if (!ok) {
        this.$message.error(`图片大小不能超过 ${this.maxSizeMb}MB`);
        return false;
      }
      return true;
    },
    handleSuccess(res, file, fileList) {
      var self = this;
      const name = this.pickRawName(res, file);
      if (!name) {
        this.$message.error("上传失败");
        return;
      }
      const rawUrl = name;
      // 先保留本地预览，再异步换成接口图
      const localUrl =
        (file && file.url) ||
        (file && file.raw ? URL.createObjectURL(file.raw) : "");
      if (localUrl && String(localUrl).indexOf("blob:") === 0) {
        this.rememberBlob(localUrl);
      }

      const nextList = (fileList || []).map(f => {
        const raw =
          f === file
            ? rawUrl
            : f.rawUrl || this.pickRawName(f.response, f) || "";
        return {
          uid: f.uid,
          name: f.name || raw,
          status: "success",
          url: f === file ? localUrl || f.url : f.url,
          rawUrl: raw,
          response: f.response
        };
      });
      this.fileList = nextList;
      this.emitValue(nextList);

      loadLeaseResourceObjectUrl(rawUrl)
        .then(function(url) {
          if (!url) return;
          self.rememberBlob(url);
          self.fileList = (self.fileList || []).map(function(f) {
            if (f.rawUrl === rawUrl) {
              return Object.assign({}, f, { url: url, status: "success" });
            }
            return f;
          });
        })
        .catch(function() {});
    },
    handleRemove(file, fileList) {
      if (this.readonly) return;
      this.fileList = fileList || [];
      this.emitValue(this.fileList);
    },
    handlePreview(file) {
      if (!file || !file.url) return;
      this.previewUrl = file.url;
      this.previewVisible = true;
    },
    handleExceed() {
      this.$message.warning(`最多上传 ${this.limit} 张图片`);
    },
    emitValue(fileList) {
      const urls = (fileList || [])
        .map(f => {
          const res = f.response || {};
          return f.rawUrl || res.name || res.url || "";
        })
        .filter(Boolean);
      const next = urls.join(",");
      this.syncing = true;
      this.$emit("input", next);
      this.$nextTick(() => {
        this.syncing = false;
      });
    }
  }
};
</script>

<style scoped>
.tip {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}
.is-readonly >>> .el-upload--picture-card {
  display: none;
}
.is-readonly >>> .el-upload-list__item-delete {
  display: none;
}
.preview-img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
}
</style>
